import useChartGrouping from '@/composables/chart-grouping';
import useDebounce from 'shared/composables/debounce';
import useGoals from '@/composables/goal/goals';
import useParamEnricher from '@/composables/view-params/enrich';
import useProperties from '@/composables/property/property';
import { AND, HAS, NOT, RESULT, TYPE, UID } from 'shared/api/query/constants';
import { DateTime } from 'luxon';
import { GoalActivityFilterHandler } from '@/lib/filter/goal-activity/handler';
import { GoalFilterHandler } from '@/lib/filter/goal/handler';
import { UpdatesFilterHandler } from '@/lib/filter/updates/handler';
import { aggregatorType, chartGroupByOption, gridPageChartType, timeSeriesInterval } from 'shared/constants.json';
import { buildCustomFuncProgressCourseListPred } from '@/api/query/custom-func-helper';
import { combine } from 'shared/api/query/filter';
import { computed, ref, watch } from 'vue';
import { copy } from 'shared/lib/copy';
import { createGoalCycleFilter } from '@/api/query/nebula/goal-cascade';
import { dogma } from '@/api';
import { goalActivity as goalActivityConfig, goal as goalConfig, update as updateConfig } from 'shared/api/query/configs.json';
import { goalsInCycleVarBlock } from '@/api/query/nebula/goal';
import { logCatch } from '@/lib/logger/logger';

const generateTimeSeries = (startDate, endDate, interval = timeSeriesInterval.weekly) => {
  const now = DateTime.local();
  const intervalConfig = { weeks: 0, months: 0 };

  switch (interval) {
    case timeSeriesInterval.monthly:
      intervalConfig.months = 1;
      break;
    case timeSeriesInterval.daily:
      intervalConfig.days = 1;
      break;
    case timeSeriesInterval.weekly:
    default:
      intervalConfig.weeks = 1;
      break;
  }

  const timeSeries = [];
  let t = startDate;

  if (now.diff(t) < 0) {
    return [t];
  }

  while (t.diff(endDate) < 0 && t.diff(now) < 0) {
    timeSeries.push(t);
    t = t.plus(intervalConfig);
  }

  if (now.diff(endDate) < 0) {
    timeSeries.push(now);
  }

  if (now.diff(endDate) > 0) {
    timeSeries.push(endDate);
  }

  return timeSeries;
};

export default function useChartDataQuery(chart) {
  const loading = ref(false);
  const resultRaw = ref(null);
  const error = ref(null);

  const { debounce } = useDebounce({ onBeforeUnmountCancel: true });

  const { processChartData } = useChartGrouping(chart, resultRaw);

  const goalFilterHandler = new GoalFilterHandler();
  const updatesFilterHandler = new UpdatesFilterHandler();
  const goalActivityFilterHandler = new GoalActivityFilterHandler();

  const propertySvc = useProperties();
  const goalsSvc = useGoals();
  const paramEnricher = useParamEnricher(propertySvc, goalsSvc);

  const enrichedFilter = computed(() => {
    if (chart.value.filter === null) {
      return null;
    }
    return paramEnricher.enrich(copy(chart.value.filter));
  });

  const filter = computed(() => {
    switch (chart.value.source) {
      case goalConfig.model: {
        const { filter: goalCycleFilter, varBlocks: goalCycleVarBlocks } = createGoalCycleFilter(chart.value.goalCycles);
        if (enrichedFilter.value === null) {
          return { filterTree: goalCycleFilter, varBlocks: goalCycleVarBlocks };
        }
        const filterObject = goalFilterHandler.Translate(enrichedFilter.value);
        return {
          filterTree: combine(AND, [goalCycleFilter, filterObject.filterTree]),
          varBlocks: [...filterObject.varBlocks, ...goalCycleVarBlocks],
        };
      }
      case updateConfig.model: {
        if (enrichedFilter.value === null) {
          return { filterTree: null, varBlocks: [] };
        }
        const filterObject = updatesFilterHandler.Translate(enrichedFilter.value);
        const checkInFilter = { op: NOT, child: [{ func: { name: HAS, attr: updateConfig.edges.goal } }] };
        return {
          filterTree: combine(AND, [checkInFilter, filterObject.filterTree]),
          varBlocks: filterObject.varBlocks,
        };
      }
      case goalActivityConfig.model: {
        if (enrichedFilter.value === null) {
          return { filterTree: null, varBlocks: [] };
        }
        const filterObject = goalActivityFilterHandler.Translate(enrichedFilter.value);
        return {
          filterTree: filterObject.filterTree,
          varBlocks: filterObject.varBlocks,
        };
      }
      default:
        return { filterTree: null, varBlocks: [] };
    }
  });

  const getEdge = (prop) => (prop.isProperty ? 'cachedProperty' : prop.edge);

  const getLangs = (prop) => (prop.isProperty ? [`p${prop.property.uid}`] : null);

  const stackableChartTypes = [gridPageChartType.line, gridPageChartType.bar, gridPageChartType.column, gridPageChartType.table];

  const appendStackOptions = (groupbyAttrs) => {
    const { stackOptions } = chart.value.aggregationConfig;
    if (!stackableChartTypes.includes(chart.value.chartType) || stackOptions.groupBy === chartGroupByOption.none) {
      return groupbyAttrs;
    }
    return [
      ...groupbyAttrs,
      { alias: 'stackGroup', attr: getEdge(stackOptions), langs: getLangs(stackOptions) },
    ];
  };

  const isTimeSeriesMode = computed(() => chart.value.aggregationConfig?.aggregation?.aggregator === aggregatorType.progressOverTime);

  const timeSeriesData = computed(() => {
    if (!isTimeSeriesMode.value || chart.value.goalCycles.length === 0) {
      return { timeSeries: [], startDate: null, endDate: null };
    }

    // Get cycle date range from selected cycles
    const cycles = chart.value.goalCycles;
    const startDate = cycles.reduce((min, cycle) => {
      const cycleStart = DateTime.fromISO(cycle.start);
      return min === null || cycleStart < min ? cycleStart : min;
    }, null);

    const endDate = cycles.reduce((max, cycle) => {
      const cycleEnd = DateTime.fromISO(cycle.end);
      return max === null || cycleEnd > max ? cycleEnd : max;
    }, null);

    if (!startDate || !endDate) {
      return { timeSeries: [], startDate: null, endDate: null };
    }

    const interval = chart.value.aggregationConfig.timeSeriesInterval || 'weekly';
    const timeSeries = generateTimeSeries(startDate, endDate, interval);

    return { timeSeries, startDate, endDate };
  });

  const query = computed(() => {
    if (isTimeSeriesMode.value) {
      const { timeSeries } = timeSeriesData.value;
      if (timeSeries.length === 0) {
        return [];
      }

      const cycleGoalsVar = 'cycle_goals';
      const goalCycles = chart.value.goalCycles;
      const goalFilter = enrichedFilter.value !== null
        ? goalFilterHandler.Translate(enrichedFilter.value)
        : { filterTree: null, varBlocks: [] };

      return [
        ...goalsInCycleVarBlock({ goalCycles, filter: goalFilter.filterTree, varName: cycleGoalsVar }),
        ...goalFilter.varBlocks,
        {
          alias: RESULT,
          model: goalConfig.model,
          needsVar: [{ name: cycleGoalsVar, typ: 1 }],
          func: { name: UID, needsVar: [{ name: cycleGoalsVar, typ: 1 }] },
          filter: goalFilter.filterTree,
          default: [],
          children: [
            buildCustomFuncProgressCourseListPred({ timeSeries, attr: UID }),
          ],
        },
      ];
    }
    switch (chart.value.chartType) {
      case gridPageChartType.metric:
        switch (chart.value.aggregationConfig.aggregation.aggregator) {
          case aggregatorType.count: {
            return [
              ...filter.value.varBlocks,
              {
                alias: 'res',
                func: { name: TYPE, args: [{ value: chart.value.source, typ: 1 }] },
                filter: filter.value.filterTree,
                model: chart.value.source,
                children: [
                  { attr: UID, isCount: true },
                ],
              },
            ];
          }
          case aggregatorType.countDistinct:
            return [
              ...filter.value.varBlocks,
              {
                alias: 'res',
                func: { name: TYPE, args: [{ value: chart.value.source, typ: 1 }] },
                filter: filter.value.filterTree,
                model: chart.value.source,
                isGroupby: true,
                groupbyAttrs: [{
                  attr: getEdge(chart.value.aggregationConfig.aggregation),
                  langs: getLangs(chart.value.aggregationConfig.aggregation),
                }],
              },
            ];
          case aggregatorType.avg:
          case aggregatorType.sum:
          case aggregatorType.min:
          case aggregatorType.max: {
            return [
              ...filter.value.varBlocks,
              {
                alias: 'var',
                func: { name: TYPE, args: [{ value: chart.value.source, typ: 1 }] },
                filter: filter.value.filterTree,
                model: chart.value.source,
                children: [
                  {
                    attr: getEdge(chart.value.aggregationConfig.aggregation),
                    langs: getLangs(chart.value.aggregationConfig.aggregation),
                    var: 'edgeVal',
                  },
                ],
              },
              {
                alias: 'res',
                children: [
                  {
                    alias: chart.value.aggregationConfig.aggregation.aggregator,
                    attr: 'val',
                    isInternal: true,
                    needsVar: [
                      {
                        name: 'edgeVal',
                        typ: 2,
                      },
                    ],
                    func: {
                      name: chart.value.aggregationConfig.aggregation.aggregator,
                      needsVar: [
                        {
                          name: 'edgeVal',
                          typ: 2,
                        },
                      ],
                    },
                  },
                ],
              },
            ];
          }
          default:
            return [];
        }
      case gridPageChartType.pie:
      case gridPageChartType.line:
      case gridPageChartType.bar:
      case gridPageChartType.table:
      case gridPageChartType.column: {
        switch (chart.value.aggregationConfig.aggregation.aggregator) {
          case aggregatorType.count:
            return [
              ...filter.value.varBlocks,
              {
                alias: 'res',
                func: { name: TYPE, args: [{ value: chart.value.source, typ: 1 }] },
                filter: filter.value.filterTree,
                model: chart.value.source,
                isGroupby: true,
                groupbyAttrs: appendStackOptions([{
                  alias: 'val',
                  attr: getEdge(chart.value.groupBy),
                  langs: getLangs(chart.value.groupBy),
                }]),
                children: [
                  { attr: UID, isCount: true },
                ],
              },
            ];
          case aggregatorType.countDistinct:
            return [
              ...filter.value.varBlocks,
              {
                alias: 'res',
                func: { name: TYPE, args: [{ value: chart.value.source, typ: 1 }] },
                filter: filter.value.filterTree,
                model: chart.value.source,
                isGroupby: true,
                groupbyAttrs: appendStackOptions([
                  {
                    alias: 'val',
                    attr: getEdge(chart.value.groupBy),
                    langs: getLangs(chart.value.groupBy),
                  },
                  {
                    alias: 'subGroup',
                    attr: getEdge(chart.value.aggregationConfig.aggregation),
                    langs: getLangs(chart.value.aggregationConfig.aggregation),
                  },
                ]),
                children: [
                  { attr: UID, isCount: true },
                ],
              },
            ];
          case aggregatorType.avg:
          case aggregatorType.sum:
          case aggregatorType.min:
          case aggregatorType.max: {
            return [
              ...filter.value.varBlocks,
              {
                alias: 'res',
                func: { name: TYPE, args: [{ value: chart.value.source, typ: 1 }] },
                filter: filter.value.filterTree,
                model: chart.value.source,
                isGroupby: true,
                groupbyAttrs: appendStackOptions([{
                  alias: 'val',
                  attr: getEdge(chart.value.groupBy),
                  langs: getLangs(chart.value.groupBy),
                }]),
                children: [
                  { attr: UID, isCount: true },
                  {
                    alias: chart.value.aggregationConfig.aggregation.aggregator,
                    attr: getEdge(chart.value.aggregationConfig.aggregation),
                    func: { name: chart.value.aggregationConfig.aggregation.aggregator },
                  },
                ],
              },
            ];
          }
          default:
            return [];
        }
      }
      default:
        return [];
    }
  });

  const decodeResponse = (data) => {
    if (isTimeSeriesMode.value) {
      const progressCourseList = data.result?.[0]?.progressCourseList;
      if (!progressCourseList || progressCourseList.length === 0) {
        return {
          isTimeSeries: true,
          progressData: [],
          startDate: timeSeriesData.value.startDate,
          endDate: timeSeriesData.value.endDate,
        };
      }

      // progressCourseList is an array of { date: string, list: [{ progress: number }, ...] }
      // We need to calculate the average progress at each time point
      const progressData = progressCourseList.map((point) => {
        const date = DateTime.fromISO(point.date);
        const progressValues = point.list?.map((g) => g.progress) ?? [];
        const avgProgress = progressValues.length > 0
          ? progressValues.reduce((sum, p) => sum + p, 0) / progressValues.length
          : 0;

        return {
          x: date.toMillis(),
          y: Math.round(avgProgress),
        };
      });

      return {
        isTimeSeries: true,
        progressData,
        startDate: timeSeriesData.value.startDate,
        endDate: timeSeriesData.value.endDate,
      };
    }

    switch (chart.value.chartType) {
      case gridPageChartType.metric:
        switch (chart.value.aggregationConfig.aggregation.aggregator) {
          case aggregatorType.count:
            return data.res[0].count;
          case aggregatorType.avg:
            return data.res[0].avg;
          case aggregatorType.sum:
            return data.res[0].sum;
          case aggregatorType.min:
            return data.res[0].min;
          case aggregatorType.max:
            return data.res[0].max;
          case aggregatorType.countDistinct:
            return data.res[0]['@groupby']?.length ?? 0;
          default:
            return 0;
        }
      case gridPageChartType.pie:
      case gridPageChartType.line:
      case gridPageChartType.bar:
      case gridPageChartType.table:
      case gridPageChartType.column: {
        if (Object.keys(data).length === 0) {
          return { series: [], total: 0 };
        }
        const groupByData = data.res?.[0]?.['@groupby'];
        if (!groupByData) {
          return { series: [], total: 0 };
        }
        switch (chart.value.aggregationConfig.aggregation.aggregator) {
          case aggregatorType.count:
          case aggregatorType.avg:
          case aggregatorType.sum:
          case aggregatorType.min:
          case aggregatorType.max:
          case aggregatorType.countDistinct:
            return processChartData(groupByData);
          default:
            return { series: [], total: 0 };
        }
      }
      default:
        return { series: [], total: 0 };
    }
  };

  const result = computed(() => {
    if (resultRaw.value === null || loading.value) {
      return null;
    }
    return decodeResponse(resultRaw.value);
  });

  const runQuery = (debounceDelay = 0) => {
    loading.value = true;
    const run = () => {
      if (query.value.length === 0) {
        loading.value = false;
        return;
      }
      dogma.query(query.value)
        .then((res) => {
          resultRaw.value = res.data;
          error.value = null;
        })
        .catch(logCatch((e) => {
          error.value = e;
        }))
        .finally(() => {
          loading.value = false;
        });
    };
    debounce(run, debounceDelay);
  };

  watch(query, (newVal, oldVal) => {
    if (JSON.stringify(newVal) === JSON.stringify(oldVal)) {
      return;
    }
    runQuery(50);
  }, { deep: true });

  runQuery();

  return {
    runQuery,
    result,
    error,
    loading,
  };
}
